import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createPreviewActions,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import { Label } from "@/core/components/ui/label";
import { Textarea } from "@/core/components/ui/textarea";
import { minorToMajor } from "@/core/utils/func/money-minor";
import { useLedgerTranslation } from "@/features/ledger/i18n/use-ledger-translation";
import {
  journalEntryDebitTotalMinor,
  type JournalEntryRow,
} from "@/features/ledger/components/journal-entries/journal-entries-columns";
import { createApprovalReasonSchema } from "@/features/shared/schemas/maker-checker.schema";

/**
 * Preview de contre-passation manuelle d'une écriture (guide §6.4) — action
 * SENSIBLE (guide §5.4) : ne peut jamais être exécutée directement. Le
 * confirm ne déclenche plus `/reverse` mais crée une demande d'approbation
 * LEDGER_MANUAL_REVERSAL (maker-checker) — la mutation est portée par
 * l'appelant (`JournalEntryRowActions` / `JournalEntryDetailPage`).
 *
 * Le motif (`reason`) est obligatoire : il est affiché à l'approbateur dans
 * la file d'approbations. L'`approvedByKeycloakId` et l'`approvalRequestId`
 * du corps de `/reverse` sont renseignés par l'approbateur au moment de
 * l'exécution (page détail d'une ApprovalRequest).
 */
export function JournalEntryReverseDialog({
  entry,
  open,
  onOpenChange,
  onConfirm,
  pending,
}: {
  entry: JournalEntryRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Confirmé avec le motif métier de la demande d'approbation. */
  onConfirm: (reason: string) => void;
  pending: boolean;
}) {
  const { t } = useLedgerTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : reconstruit le schéma zod traduit à chaque changement de langue.
  const reasonSchema = useMemo(() => createApprovalReasonSchema(), [i18n.language]);

  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | undefined>();

  if (!open) return null;

  const totalMinor = journalEntryDebitTotalMinor(entry.lines);

  const handleConfirm = () => {
    const parsed = reasonSchema.safeParse({ reason });
    if (!parsed.success) {
      setReasonError(parsed.error.issues[0]?.message);
      return;
    }
    setReasonError(undefined);
    onConfirm(parsed.data.reason.trim());
  };

  return (
    <ShareablePreview
      data={{
        reference: entry.reference,
        productCode: entry.productCode,
        sourceService: entry.sourceService,
        sourceTransactionRef: entry.sourceTransactionRef,
        status: entry.status,
        businessDate: entry.businessDate,
      }}
      title={t("ledger.journal.preview.reverseTitle")}
      description={t("ledger.journal.preview.reverseDescription")}
      mode="user"
      total={
        totalMinor != null
          ? {
              label: t("ledger.journal.preview.totalDebited"),
              amount: minorToMajor(totalMinor, entry.currency),
              currency: entry.currency,
            }
          : undefined
      }
      defaultOpen
      onOpenChange={(isOpen) => {
        if (!isOpen && !pending) onOpenChange(false);
      }}
      maxHeight="90vh"
      customView={() => (
        <div className="space-y-3">
          <div>
            <Label htmlFor="reverseReason">
              {t("ledger.journal.preview.reason")}
            </Label>
            <Textarea
              id="reverseReason"
              className="mt-1"
              placeholder={t("ledger.journal.preview.reasonPlaceholder")}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            {reasonError ? (
              <p className="text-sm text-destructive">{reasonError}</p>
            ) : null}
          </div>
        </div>
      )}
      actions={createPreviewActions({
        onConfirm: handleConfirm,
        onCancel: () => onOpenChange(false),
        t,
        confirmLoading: pending,
        withIndicatorOnConfirm: true,
      })}
    />
  );
}
